import { useEffect, useRef, useState } from "react";

import {
  CrudMode,
  useParams,
  Page,
  PageRoute,
  buildPath,
  buildTemplatePath,
  pn,
  useValidationErrors,
  useUnsavedChanges,
  buildOptionalParams,
  Grid,
  Button,
  Stack,
  Typography,
  RichTextEditor,
  GridBreak,
  Card,
} from "@compass/core";
import { RootState } from "redux/store";
import {
  Permission,
  StudentApplicationListResource,
  StudentApplicationResponseDto,
  PublicApplicationViewResource,
  PublicApplicationResponseDto,
  Student,
  StudentEnrollment as StudentEnrollmentModel,
  StudentViewResource,
  StudentEditResource,
  ApplicationQuestion,
  ApplicationQuestionResponse,
  Address,
  Parent,
  StudentParent,
  ApplicationSection,
  Application,
  TenantVariableStudent,
  LkStudentDisplayFieldId,
} from "generated/openApi";
import produce from "immer";
import ApiService from "services/apiService";
import { createEntity } from "utils";
import PreviewIcon from "@mui/icons-material/Preview";
import { StudentApplicationHeader } from "./StudentApplicationHeader";
import { PublicApplicationQuestionPage } from "pages/public-forms/applications/PublicApplicationQuestionPage";
import { useSelector } from "react-redux";
import { TransientFormAnswer, TransientQuestionResponse } from "pages/public-forms/common/TransientQuestionResponse";
import { BasicInfo } from "pages/students/students/StudentPage/StudentProfile/BasicInfo";
import { ApplicationProperty, ApplicationStatus } from "pages/cobro/applications/ApplicationPropertyEnum";
import ReactDOMServer from "react-dom/server";
import { StudentApplicationFiles } from "./StudentApplicationFiles";
import { Select } from "components";
import { StudentEnrollment } from "pages/students/students/StudentCreatePage/StudentEnrollment";
import { StudentApplicationEvents } from "./StudentApplicationEvents";
import { useReactToPrint } from "react-to-print";

const routeName = "studentApplication";

class Params {
  applicationResponseId: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  test: any;
}

export const studentApplicationPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Application",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentApplicationPage = () => {
  const { params } = useParams(routeName, Params);
  const ROOT_PERMISSION: Permission = Permission.StudentApplication;

  // const [applicationResponse, setApplicationResponse] = useState<ApplicationResponse>(createEntity<ApplicationResponse>({}));
  const [applicationDto, setApplicationDto] = useState<StudentApplicationResponseDto>();
  const [application, setApplication] = useState<Application>({} as Application);
  const [applicationViewResource, setApplicationViewResource] = useState<PublicApplicationViewResource>();
  const [transientQuestionResponses, setTransientQuestionResponses] = useState<TransientQuestionResponse[]>([]);
  const [resource, setResource] = useState<StudentApplicationListResource>({});
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  const [canReview, setCanReview] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [hasPermissionToApprove, setHasPermissionToApprove] = useState<boolean>(false);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const { unsavedChanges } = useUnsavedChanges({
    trackedItems: [applicationDto?.applicationResponse],
  });

  // for branching
  const [hasBranches, setHasBranches] = useState<boolean>(false);
  const [activeQuestions, setActiveQuestions] = useState<ApplicationQuestion[]>([]);

  // approving / new student
  const [newStudent, setNewStudent] = useState<Student>();
  const [newStudentViewResource, setNewStudentViewResource] = useState<StudentViewResource>({});
  const [newStudentEditResource, setNewStudentEditResource] = useState<StudentEditResource>(Object);

  // for reviewing workflow
  const [reviewStudentEnrollment, setReviewStudentEnrollment] = useState<StudentEnrollmentModel>();

  // for printing/downloading file
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const appTenantName = useSelector((state: RootState) => state.app.tenantName);

  const applicationQuestions = () =>
    (application?.applicationSections?.flatMap((section) => section.applicationQuestions || []).filter(Boolean) ||
      []) as ApplicationQuestion[];

  const hasDisplayField = (fieldId: LkStudentDisplayFieldId) => resource.displayFields?.some((x) => x.id === fieldId);

  useEffect(() => {
    fetchResource();
  }, []);

  useEffect(() => {
    if (hasBranches == true) {
      initializeApplicationQuestions();
    }
    else {

    }
  }, [hasBranches]);

  const formatDate = (newDate) => {
    var date = new Date(newDate);
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Get month (adding 1 since January is 0-indexed), pad with leading zero if necessary
    const day = date.getDate().toString().padStart(2, "0"); // Get day of the month, pad with leading zero if necessary
    const year = date.getFullYear(); // Get full year
    return `${month}/${day}/${year}`; // Return formatted string
  };

  // setup transient question responses
  useEffect(() => {
    const transientQuestionResponses: TransientQuestionResponse[] = [];

    applicationDto?.applicationResponse?.application?.applicationSections?.map((s) => {
      s.applicationQuestions?.map((q) => {
        transientQuestionResponses.push({
          questionId: q.id,
          writeIn: null,
          writeInDate: null,
          writeInDecimal: null,
          formAnswers: [],
          signatureFile: null,
          fileUpload: null,
        });
      });
    });

    applicationDto?.applicationResponse?.applicationQuestionResponses?.forEach((r) => {
      var mappedResponse = transientQuestionResponses.find((t) => t.questionId == r.applicationQuestionId);
      if (mappedResponse) {
        if (r.applicationAnswerId != null || r.applicationPropertyEntityId) {
          var applicationAnswer: TransientFormAnswer = {
            formAnswerId: r.applicationAnswerId,
            formEntityId: r.applicationPropertyEntityId,
            subQuestionId: r.applicationSubQuestionId,
            writeIn: r.writeIn,
          };
          mappedResponse?.formAnswers.push(applicationAnswer);
        } else {
          mappedResponse.writeIn = r.writeIn || null;
          mappedResponse.writeInDate = r.writeInDate != null ? formatDate(r.writeInDate) : null;
          mappedResponse.fileUpload = r.fileUploadFile || null;
        }
      }
    });

    if (!hasBranches && applicationDto && applicationDto.applicationResponse) {
      setActiveQuestions(applicationDto.applicationResponse.application!.applicationSections!.flatMap((x) => x.applicationQuestions!));
    }

    setTransientQuestionResponses(transientQuestionResponses);
  }, [applicationDto]);

  useEffect(() => {
    ApiService.students.getViewResource(0).then((response) => {
      setNewStudentViewResource(response);
    });
    ApiService.students.getEditResource().then((response) => {
      setNewStudentEditResource(response);
    });
  }, []);

  useEffect(() => {
    if (isReviewing) {
      if (!applicationDto?.studentId) return;

      ApiService.students.get(applicationDto?.studentId).then((student) => {
        setNewStudent(student);

        ApiService.studentEnrollments.getEnrollmentByStudentId(applicationDto?.studentId).then((response) => {
          setReviewStudentEnrollment(response);
        });
      });
    }
    else if (isApproving && applicationDto) {
      ApiService.students.get(0).then((student) => {
        student.creatingFirstEnrollment = {} as StudentEnrollmentModel;
        student.creatingFirstEnrollment.studentId = 0;
        student.creatingStudentParents = [];
        student.address = {} as Address;

        // student
        var studentId = findResponseByPropertyId(ApplicationProperty.StudentID);
        student.customId1 = studentId?.writeIn || "";
        var firstName = findResponseByPropertyId(ApplicationProperty.FirstName);
        student.firstName = firstName?.writeIn || "";
        var middleName = findResponseByPropertyId(ApplicationProperty.MiddleName);
        student.middleName = middleName?.writeIn || "";
        var lastName = findResponseByPropertyId(ApplicationProperty.LastName);
        student.lastName = lastName?.writeIn || "";
        var dateOfBirth = findResponseByPropertyId(ApplicationProperty.DateOfBirth);
        if (dateOfBirth != null) {
          student.dateOfBirth = new Date(dateOfBirth.writeInDate || dateOfBirth.writeIn!);
        }
        var gender = findResponseByPropertyId(ApplicationProperty.Gender);
        student.genderId = gender?.applicationPropertyEntityId || gender?.applicationAnswer?.entityId;
        var ethnicity = findResponseByPropertyId(ApplicationProperty.Ethnicity);
        student.ethnicityId = ethnicity?.applicationPropertyEntityId || ethnicity?.applicationAnswer?.entityId;
        var race = findResponseByPropertyId(ApplicationProperty.Race);
        student.raceId = race?.applicationPropertyEntityId || race?.applicationAnswer?.entityId;
        var email = findResponseByPropertyId(ApplicationProperty.Email);
        student.email = email?.writeIn || "";
        var phone = findResponseByPropertyId(ApplicationProperty.PhoneNumber);
        student.phone = phone?.writeIn || "";
        var cellPhone = findResponseByPropertyId(ApplicationProperty.CellPhone);
        student.cellPhone = cellPhone?.writeIn || "";
        var graduationYear = findResponseByPropertyId(ApplicationProperty.GraduationYear);
        if (graduationYear != null && graduationYear.writeIn != null) {
          student.graduationYear = Number.parseInt(graduationYear.writeIn!) || undefined;
        }

        var firstGeneration = findResponseByPropertyId(ApplicationProperty.FirstGeneration);
        if (firstGeneration != null && firstGeneration.applicationPropertyEntityId != null) {
          student.firstGeneration = firstGeneration.applicationPropertyEntityId == 1;
        }

        student.enrollmentDate = new Date();

        // address
        var city = findResponseByPropertyId(ApplicationProperty.City);
        student.address.city = city?.writeIn || "";
        var zipcode = findResponseByPropertyId(ApplicationProperty.ZipCode);
        student.address.postal = zipcode?.writeIn || "";
        var state = findResponseByPropertyId(ApplicationProperty.State);
        student.address.state = state?.writeIn || "";
        var address = findResponseByPropertyId(ApplicationProperty.Address);
        student.address.addressLine = address?.writeIn || "";

        // enrollment
        student.creatingFirstEnrollment.startDate = new Date();
        var currentYear = newStudentViewResource.activeSchoolYear;
        if (currentYear) {
          student.creatingFirstEnrollment.year = currentYear;
        }

        student.creatingFirstEnrollment.participantStatusId = 1;

        var school = findResponseByPropertyId(ApplicationProperty.School);
        if (school != null && (school.applicationPropertyEntityId != null || school.applicationAnswer?.entityId != null)) {
          student.creatingFirstEnrollment.schoolId = school.applicationPropertyEntityId || school.applicationAnswer?.entityId!;
        }
        var grade = findResponseByPropertyId(ApplicationProperty.Grade);
        if (grade != null && (grade.applicationPropertyEntityId != null || grade.applicationAnswer?.entityId != null)) {
          student.creatingFirstEnrollment.gradeLevelId = grade.applicationPropertyEntityId!;
        }

        var fosterCare = findResponseByPropertyId(ApplicationProperty.FosterCare);
        if (fosterCare != null && fosterCare.applicationPropertyEntityId != null) {
          student.creatingFirstEnrollment.fosterCare = fosterCare.applicationPropertyEntityId == 1;
        }

        var homeless = findResponseByPropertyId(ApplicationProperty.Homeless);
        if (homeless != null && homeless.applicationPropertyEntityId != null) {
          student.creatingFirstEnrollment.homeless = homeless.applicationPropertyEntityId == 1;
        }

        var lepId = findResponseByPropertyId(ApplicationProperty.LEP);
        if (lepId != null && (lepId.applicationPropertyEntityId != null || lepId.applicationAnswer?.entityId != null)) {
          student.creatingFirstEnrollment.lepId = lepId.applicationPropertyEntityId || lepId.applicationAnswer?.entityId;
        }

        // parent #1
        var p1FirstName = findResponseByPropertyId(ApplicationProperty.Parent1_FirstName);
        var p1LastName = findResponseByPropertyId(ApplicationProperty.Parent1_LastName);
        var p1Email = findResponseByPropertyId(ApplicationProperty.Parent1_Email);
        var p1HomePhone = findResponseByPropertyId(ApplicationProperty.Parent1_HomePhoneNumber);
        var p1CellPhone = findResponseByPropertyId(ApplicationProperty.Parent1_CellPhoneNumber);
        var p1City = findResponseByPropertyId(ApplicationProperty.Parent1_City);
        var p1State = findResponseByPropertyId(ApplicationProperty.Parent1_State);
        var p1Postal = findResponseByPropertyId(ApplicationProperty.Parent1_ZipCode);
        var p1Address = findResponseByPropertyId(ApplicationProperty.Parent1_Address);
        var p1Relationship = findResponseByPropertyId(ApplicationProperty.Parent1_Relationship);
        var p1Education = findResponseByPropertyId(ApplicationProperty.Parent1_HighestCompletedEducation);

        if (p1FirstName != null && p1LastName != null && p1FirstName.writeIn != null && p1LastName.writeIn != null) {
          let parent1 = createEntity<StudentParent>({});
          parent1.parent = createEntity<Parent>({});
          parent1.parent.address = createEntity<Address>({});

          parent1.parent.firstName = p1FirstName.writeIn || "";
          parent1.parent.lastName = p1LastName.writeIn || "";
          parent1.parent.email = p1Email?.writeIn || "";
          parent1.parent.phoneNumber = p1HomePhone?.writeIn || "";
          parent1.parent.cellPhone = p1CellPhone?.writeIn || "";
          parent1.relationId = p1Relationship?.applicationPropertyEntityId || p1Relationship?.applicationAnswer?.entityId;

          parent1.parent.address.city = p1City?.writeIn || "";
          parent1.parent.address.state = p1State?.writeIn || "";
          parent1.parent.address.postal = p1Postal?.writeIn || "";
          parent1.parent.address.addressLine = p1Address?.writeIn || "";

          if (p1Education != null && (p1Education.applicationPropertyEntityId != null || p1Education.applicationAnswer?.entityId != null)) {
            parent1.parent.educationLevelId = p1Education.applicationPropertyEntityId || p1Education.applicationAnswer?.entityId!;
          }

          student.creatingStudentParents?.push(parent1);
        }

        // parent #2
        var p2FirstName = findResponseByPropertyId(ApplicationProperty.Parent2_FirstName);
        var p2LastName = findResponseByPropertyId(ApplicationProperty.Parent2_LastName);
        var p2Email = findResponseByPropertyId(ApplicationProperty.Parent2_Email);
        var p2HomePhone = findResponseByPropertyId(ApplicationProperty.Parent2_HomePhoneNumber);
        var p2CellPhone = findResponseByPropertyId(ApplicationProperty.Parent2_CellPhoneNumber);
        var p2City = findResponseByPropertyId(ApplicationProperty.Parent2_City);
        var p2State = findResponseByPropertyId(ApplicationProperty.Parent2_State);
        var p2Postal = findResponseByPropertyId(ApplicationProperty.Parent2_ZipCode);
        var p2Address = findResponseByPropertyId(ApplicationProperty.Parent2_Address);
        var p2Relationship = findResponseByPropertyId(ApplicationProperty.Parent2_Relationship);
        var p2Education = findResponseByPropertyId(ApplicationProperty.Parent2_HighestCompletedEducation);

        if (p2FirstName != null && p2LastName != null && p2FirstName.writeIn != null && p2LastName.writeIn != null) {
          let parent2 = {} as StudentParent;
          parent2.parent = {} as Parent;
          parent2.parent.address = {} as Address;

          parent2.parent.firstName = p2FirstName.writeIn || "";
          parent2.parent.lastName = p2LastName.writeIn || "";
          parent2.parent.email = p2Email?.writeIn || "";
          parent2.parent.phoneNumber = p2HomePhone?.writeIn || "";
          parent2.parent.cellPhone = p2CellPhone?.writeIn || "";
          parent2.relationId = p2Relationship?.applicationPropertyEntityId || p2Relationship?.applicationAnswer?.entityId;

          parent2.parent.address.city = p2City?.writeIn || "";
          parent2.parent.address.state = p2State?.writeIn || "";
          parent2.parent.address.postal = p2Postal?.writeIn || "";
          parent2.parent.address.addressLine = p2Address?.writeIn || "";

          if (p2Education != null && (p2Education.applicationPropertyEntityId != null || p2Education.applicationAnswer?.entityId != null)) {
            parent2.parent.educationLevelId = p2Education.applicationPropertyEntityId || p2Education.applicationAnswer?.entityId!;
          }

          student.creatingStudentParents?.push(parent2);
        }

        setNewStudent(student);
      });
    }
  }, [isApproving, isReviewing, applicationDto?.studentId]);

  const initializeApplicationQuestions = () => {
    if (!hasBranches) {

    }
    else {
      // set up initial active questions (i.e questions with no branchedFromFormAnswer or branchedFromFormQuestion)
      var hasNext: boolean = true;
      var activeQuestions: ApplicationQuestion[] = [];
      var activeSections: ApplicationSection[] = [];

      // set up initial
      applicationQuestions().forEach((q) => {
        if (q && !questionIsBranched(q)) {
          activeQuestions.push(q);
        }
      });
      // survey?.formSections?.forEach((s) => {
      //   if (!sectionIsBranched(s)) {
      //     activeSections.push(s);
      //   }
      // });

      while (hasNext) {
        // handle questions first
        var nextActiveQuestions = findActiveBranchedQuestions(activeQuestions);

        // if no new active questions
        if (nextActiveQuestions == activeQuestions) {
          hasNext = false;
        }
        else {
          // otherwise mark them as active
          if (nextActiveQuestions != activeQuestions) {
            activeQuestions = activeQuestions.concat(nextActiveQuestions);
          }
        }

        const temp: ApplicationQuestion[] = [];
        activeQuestions.map((n) => {
          const parentQuestionIds = findParentQuestionIds(n);
          var branchIndex = temp.findIndex((t) => parentQuestionIds.some((p) => p == t.id));
          if (branchIndex != - 1) {
            temp.splice(branchIndex + 1, 0, n);
          }
          else {
            temp.push(n);
          }
        })
        activeQuestions = temp;
      }

      setActiveQuestions(activeQuestions);
    }
  }

  const programStatusField = () => {
    // const property = properties.find((x) => x.propertyName === propertyName);

    return <Select
      required
      label="Program Status"
      errors={validationErrors["programStatusId"]}
      value={applicationViewResource?.programStatuses!.find((x) => x.id === applicationDto?.applicationResponse?.programStatusId)}
      getOptionLabel={(option) => `${option.name}`}
      options={applicationViewResource?.programStatuses}
      onChange={(e, value) => {
        setApplicationDto(
          produce(applicationDto, (draft) => {
            draft!.applicationResponse!.programStatusId = value!.id;
          })
        );
        setHasChanges(true);
      }}
    />
  };

  const reviewerField = () => {
    return <Select
      label="Reviewer"
      errors={validationErrors["reviewerId"]}
      value={resource?.reviewers?.find((x) => x.id === applicationDto?.applicationResponse?.reviewerId)}
      getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
      options={resource?.reviewers}
      onChange={(e, value) => {
        setApplicationDto(
          produce(applicationDto, (draft) => {
            draft!.applicationResponse!.reviewerId = value?.id;
          })
        );
        setHasChanges(true);
      }}
    />
  };

  const findParentQuestionIds = (question: ApplicationQuestion): number[] => {
    const parentQuestionIds: number[] = [];
    if (question.branchedFromApplicationQuestionIds && question.branchedFromApplicationQuestionIds.length > 0) {
      question.branchedFromApplicationQuestionIds!.map((q) => {
        parentQuestionIds.push(q);
      })
    }
    if (question.branchedFromApplicationAnswerIds && question.branchedFromApplicationAnswerIds.length > 0) {
      question.branchedFromApplicationAnswerIds!.map((a) => {
        const applicationQuestion = applicationQuestions().find(q => q && q.applicationAnswers?.some((fa) => fa.id == a));
        parentQuestionIds.push(applicationQuestion!.id);
      })
    }
    return parentQuestionIds;
  }

  const questionIsBranched = (question: ApplicationQuestion): boolean => {
    if ((question.branchedFromApplicationAnswerIds != null && question.branchedFromApplicationAnswerIds.length > 0) ||
      (question.branchedFromApplicationQuestionIds != null && question.branchedFromApplicationQuestionIds.length > 0) ||
      (question.branchedFromApplicationSectionIds != null && question.branchedFromApplicationSectionIds.length > 0))
      return true;
    else return false;
  }

  const findActiveBranchedQuestions = (activeQuestions: ApplicationQuestion[]): ApplicationQuestion[] => {
    var nextActiveQuestions: ApplicationQuestion[] = [];
    var activeQuestionIds = activeQuestions.flatMap(q => q.id);
    var activeFormAnswerIds: number[] = [];

    transientQuestionResponses.forEach((t) => {
      var answers = t.formAnswers.flatMap(x => x.formAnswerId);
      var newAnswers = answers.filter(a => a && !activeFormAnswerIds.includes(a));
      if (newAnswers.length > 0) {
        activeFormAnswerIds = activeFormAnswerIds.concat(newAnswers as number[]);
      }
    })

    var branchingQuestions = applicationQuestions().filter(q => questionIsBranched(q!));

    if (!branchingQuestions || branchingQuestions.length == 0) {
      return activeQuestions;
    }

    branchingQuestions.forEach((q) => {
      // check for branching question
      if (q && q.branchedFromApplicationQuestionIds?.some(b => activeQuestionIds.includes(b))) {
        if (!activeQuestionIds.includes(q.id)) {
          nextActiveQuestions.push(q);
        }
      }

      // check for branching answers
      if (q && q.branchedFromApplicationAnswerIds != null && q.branchedFromApplicationAnswerIds.length > 0) {
        if (activeFormAnswerIds.some(a => q.branchedFromApplicationAnswerIds?.some(b => b.toString() == a.toString()))) {
          if (!activeQuestionIds.includes(q.id)) {
            nextActiveQuestions.push(q);
          }
        }
      }
    })

    if (nextActiveQuestions.length > 0) {
      return nextActiveQuestions;
    }

    return activeQuestions;
  }

  const findQuestionByPropertyId = (applicationPropertyId: number): ApplicationQuestion | null => {
    let applicationQuestion: ApplicationQuestion | null = null;
    applicationDto!.applicationResponse!.application!.applicationSections?.forEach((section) => {
      var question = section.applicationQuestions?.find((q) => q.applicationPropertyId == applicationPropertyId);
      if (question) return (applicationQuestion = question);
    });

    return applicationQuestion;
  };

  const findResponseByPropertyId = (applicationPropertyId: number): ApplicationQuestionResponse | null => {
    let q = findQuestionByPropertyId(applicationPropertyId);

    if (q != null) {
      let matchedResponse = applicationDto?.applicationResponse?.applicationQuestionResponses?.find(
        (r) => r.applicationQuestionId == q?.id
      );
      if (matchedResponse != null) {
        return matchedResponse;
      }
    }

    // if (q != null) {
    //   let matchedResponse = transientQuestionResponses.find(t => t.questionId == q?.id);
    //   if (matchedResponse != null) {
    //     return matchedResponse;
    //   }
    // }

    return null;
  };

  const fetchApplication = (applicationResponseId: number, listResource?: StudentApplicationListResource) => {
    ApiService.studentApplications.getApplicationResponse(applicationResponseId).then((response) => {
      setApplicationDto(response);

      const isPartialDisplayField = listResource?.displayFields?.some((x) => x.id === LkStudentDisplayFieldId.IsPartial);
      if (response.applicationStatusId == ApplicationStatus.StaffReview && isPartialDisplayField) {
        setIsReviewing(true);
      }

      ApiService.publicApplication.getApplication(response.applicationResponse?.application?.guid, appTenantName).then((response) => {
        setApplication(response.application!);
        setHasBranches(response.hasBranches);
      });

      ApiService.publicApplication
        .getApplicationViewResource(response.applicationResponse?.application?.guid, appTenantName)
        .then((viewR) => {
          setApplicationViewResource(viewR);
        });
    });
  };

  const fetchResource = async () => {
    const response = await ApiService.studentApplications.getListResource();
    await setResource(response);

    const isPartialDisplayField = response?.displayFields?.some((x) => x.id === LkStudentDisplayFieldId.IsPartial);

    // If "Is Partial" is true and the tenant has the "Staff Review" Application Status enabled, set "Can Review" to true
    if (isPartialDisplayField && response.applicationStatuses?.some(a => a.id == ApplicationStatus.StaffReview)) {
      setCanReview(true);
    }

    fetchApplication(params.applicationResponseId, response);
  };

  const submitResponse = (applicationResponse: PublicApplicationResponseDto) => { };

  const updateApplicationDto = (applicationDto: StudentApplicationResponseDto) => {
    setHasChanges(true);
    setApplicationDto(applicationDto);
  };

  const updateTransientResponse = (questionResponse: TransientQuestionResponse) => {
    setTransientQuestionResponses(
      produce((draft) => {
        let draftQuestionResponseIndex = draft.findIndex((q) => q.questionId == questionResponse.questionId);

        draft[draftQuestionResponseIndex] = questionResponse;
      })
    );
  };

  const prepPrint = async () => {
    // const temp: StudentApplicationResponseDto = {
    //   pageNumber: currentPage?.pageNumber || 1,
    //   sections: surveyPages?.flatMap(p => p.sections) || []
    // };
    // await setCurrentPage(temp);
    await setIsPrinting(true);
  }

  const finishPrint = async () => {
    reactToPrintFn();
    // const temp: StudentApplicationResponseDto = {
    //   pageNumber: currentPage?.pageNumber || 1,
    //   sections: surveyPages?.find(p => p.pageNumber == pageIndex)?.sections || []
    // };
    // await setCurrentPage(temp);
    await setIsPrinting(false);
  }

  const openApplicationPreview = () => {
    const width = 1000;
    const height = 1600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},status=no,toolbar=no,menubar=no,scrollbars=yes,resizable=yes`;

    const route = `/studentApplications/${studentApplicationPageRoute.getPath({
      applicationResponseId: params.applicationResponseId,
      crudMode: "view",
    })}`;

    window.open(route, "_blank", features);
  };

  const updateEnrollment = (enrollment: StudentEnrollmentModel) => {
    if (reviewStudentEnrollment) {
      setReviewStudentEnrollment(enrollment);
      return;
    }

    setNewStudent(
      produce(newStudent!, (draft) => {
        draft.creatingFirstEnrollment = enrollment;
      })
    );
  };

  const showApplicationResponse = applicationDto && applicationViewResource && !isApproving;
  const showStudentReviewForm = isApproving && newStudentEditResource != null && newStudentViewResource != null;

  return (
    <Page
      isForm
      header={
        <StudentApplicationHeader
          rootPermission={ROOT_PERMISSION}
          crudMode={params.crudMode}
          applicationDto={applicationDto}
          isApproving={isApproving}
          printApplication={prepPrint}
          canReview={canReview}
          isReviewing={isReviewing}
          applicationResource={resource}
          updateIsApproving={setIsApproving}
          updateApplicationDto={updateApplicationDto}
          setValidationErrors={setValidationErrors}
          hasChanges={hasChanges}
          unsavedChanges={unsavedChanges}
          newStudent={newStudent || ({} as Student)}
          reviewStudentEnrollment={reviewStudentEnrollment}
        />
      }
    >
      <Grid container spacing="sm">
        <Grid item xs={12}>
          {showApplicationResponse && (
            <div ref={contentRef}>
              <PublicApplicationQuestionPage
                isViewMode={true}
                isReviewMode={false}
                activeQuestions={activeQuestions}
                isPrinting={isPrinting}
                printCallBack={finishPrint}
                application={applicationDto.applicationResponse?.application!}
                applicationResponse={applicationDto.applicationResponse}
                viewResource={applicationViewResource}
                transientQuestionResponses={transientQuestionResponses}
                updateQuestionResponse={updateTransientResponse}
                submitResponse={submitResponse}
                validationErrors={validationErrors}
              />
            </div>
          )}
        </Grid>
        <Grid item xs={12}>
          {showStudentReviewForm && (
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Button onClick={openApplicationPreview}>
                  <Stack direction="row">
                    <PreviewIcon />
                    <Typography variant="inherit" sx={{ paddingLeft: "10px" }}>
                      View Application
                    </Typography>
                  </Stack>
                </Button>
              </Grid>

              <Grid item xs={12}>
                <BasicInfo
                  student={newStudent || createEntity<Student>({})}
                  setStudent={setNewStudent}
                  tenantTextingInfo={undefined}
                  crudMode="edit"
                  // unsavedChanges={unsavedChanges}
                  // save={save}
                  fromStudentApplication={true}
                  viewResource={newStudentViewResource}
                  editResource={newStudentEditResource}
                  validationErrors={validationErrors}
                />
              </Grid>
              <Grid item xs={12}>
                <StudentEnrollment
                  enrollment={reviewStudentEnrollment || newStudent?.creatingFirstEnrollment || ({} as StudentEnrollmentModel)}
                  setEnrollment={updateEnrollment}
                  validationErrors={validationErrors[pn<Student>("creatingFirstEnrollment")] || {}}
                />
              </Grid>
              <Grid item xs={12}>
                <StudentApplicationFiles initialLoad={true} applicationResponseId={params.applicationResponseId} />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Card id="review-card">
            <Grid container spacing="sm">
              {hasDisplayField(LkStudentDisplayFieldId.ProgramStatus) && (
                <Grid item xs={4}>
                  {programStatusField()}
                </Grid>
              )}
              {canReview && (
                <Grid item xs={4}>
                  {reviewerField()}
                </Grid>
              )}
              <GridBreak />
              <Grid item xs={6}>
                {applicationDto && (<RichTextEditor
                  label="Reviewer's Notes"
                  hasColor
                  hasAlign
                  value={applicationDto?.applicationResponse?.notes}
                  onChange={(value) => {
                    setApplicationDto(
                      produce(applicationDto, (draft) => {
                        draft!.applicationResponse!.notes = value;
                      })
                    );
                    setHasChanges(true);
                  }
                  }
                />)}
              </Grid>
              <GridBreak />
              <Grid item xs={12}>
                <Stack spacing="md">
                  <Typography variant="h5">History:</Typography>
                  <StudentApplicationEvents events={applicationDto?.events} />
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Page >
  );
};
